import { Fragment, useState } from "react";
import { Link } from "react-router";
import { ChevronDown, CircleCheck, CircleMinus, CircleX, History } from "lucide-react";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, EmptyState, Skeleton, cn } from "@ncp/ui";
import { useSurfaceLinks } from "../../lib/links";
import { useAutomationOptions, type AutomationRun } from "./api";

export const RUN_VARIANT: Record<string, "success" | "danger" | "default" | "info"> = { Succeeded: "success", Failed: "danger", Skipped: "default", DryRun: "info" };

const TRIGGER_MODE: Record<string, string> = { "schedule.cron": "schedule", manual: "manual", "webhook.inbound": "webhook" };

/** Run log: what fired an automation, on which case, what each action did, and how long it took. */
export function RunsTable({ runs, loading, showAutomation = true }: { runs: AutomationRun[]; loading?: boolean; showAutomation?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { data: options } = useAutomationOptions();
  const { openInAssistant, openInBackoffice } = useSurfaceLinks();
  const [open, setOpen] = useState<string | null>(null);
  const triggerLabel = (key: string) => {
    const event = options?.catalog.events.find((e) => e.key === key);
    if (event) return locale === "ar" ? event.labelAr : event.label;
    return TRIGGER_MODE[key] ? t(`admin:automations.trigger.modes.${TRIGGER_MODE[key]}`) : key;
  };
  if (loading) return <div className="space-y-2"><Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" /></div>;
  if (runs.length === 0) return <EmptyState icon={History} title={t("admin:automations.runs.empty")} description={t("admin:automations.runs.emptyHint")} />;
  return (
    <div className="overflow-hidden rounded-2xl border border-sand-200 bg-white/80">
      <table className="w-full text-sm">
        <thead className="bg-sand-50 text-xs text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-start font-medium">{t("admin:common.status")}</th>
            {showAutomation ? <th className="px-3 py-2 text-start font-medium">{t("admin:automations.columns.automation")}</th> : null}
            <th className="px-3 py-2 text-start font-medium">{t("admin:automations.runs.trigger")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("admin:automations.runs.case")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("admin:automations.runs.actions")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("admin:automations.runs.duration")}</th>
            <th className="px-3 py-2 text-start font-medium">{t("admin:automations.runs.when")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sand-100">
          {runs.map((r) => {
            const actions = r.actions ?? [];
            const expanded = open === r.id;
            const name = (locale === "ar" && r.automationNameAr ? r.automationNameAr : r.automationName) || r.automationKey;
            const trigger = r.eventType ?? r.trigger;
            return (
              <Fragment key={r.id}>
                <tr className={cn("cursor-pointer hover:bg-aegold-50/40", expanded && "bg-sand-50/60")} onClick={() => setOpen(expanded ? null : r.id)} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") setOpen(expanded ? null : r.id); }} aria-expanded={expanded}>
                  <td className="px-4 py-2.5"><Badge variant={RUN_VARIANT[r.status] ?? "default"} size="sm" dot>{t(`admin:automations.runStatus.${r.status}`, { defaultValue: r.status })}</Badge></td>
                  {showAutomation ? (
                    <td className="px-3 py-2.5">
                      <Link to={`/automations/${encodeURIComponent(r.automationKey)}?tab=runs`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>{name}</Link>
                      <div className="text-[11px] text-muted-foreground">{t(`admin:automations.scope.${r.scope}`)}</div>
                    </td>
                  ) : null}
                  <td className="px-3 py-2.5 text-xs"><span title={trigger}>{triggerLabel(trigger)}</span>{r.triggeredBy ? <div className="text-muted-foreground">{r.triggeredBy}</div> : null}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">
                    {r.caseId && r.caseNumber ? (
                      <button type="button" className="text-aegold-800 hover:underline"
                        onClick={(e) => { e.stopPropagation(); if (r.isMatter) openInAssistant(`/matters/${r.caseId}`); else openInBackoffice(`/cases/${r.caseId}`); }}>
                        {r.caseNumber}
                      </button>
                    ) : (r.caseNumber ?? "—")}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className="inline-flex items-center gap-1">
                      {actions.length} <ChevronDown className={cn("size-3.5 text-muted-foreground transition", expanded && "rotate-180")} />
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-end text-xs tabular text-muted-foreground">{r.durationMs} ms</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{formatRelative(r.createdAt, locale)}</td>
                </tr>
                {expanded ? (
                  <tr className="bg-sand-50/60">
                    <td colSpan={showAutomation ? 7 : 6} className="px-4 pb-3 pt-0">
                      {r.error ? <p className="mb-1.5 text-xs text-danger-700">{r.error}</p> : null}
                      {!r.conditionPassed ? <p className="mb-1.5 text-xs text-muted-foreground">{t("admin:automations.runs.conditionFailed")}</p> : null}
                      {actions.length ? (
                        <ul className="space-y-1 text-xs">
                          {actions.map((a, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              {a.status === "Succeeded" ? <CircleCheck className="mt-0.5 size-3.5 text-success-600" /> : a.status === "Failed" ? <CircleX className="mt-0.5 size-3.5 text-danger-600" /> : <CircleMinus className="mt-0.5 size-3.5 text-sand-500" />}
                              <code className="font-mono text-muted-foreground" dir="ltr">{a.type}</code>
                              <span className="min-w-0 flex-1">{a.detail ?? t(`admin:automations.runStatus.${a.status}`, { defaultValue: a.status })}</span>
                              {typeof a.durationMs === "number" ? <span className="tabular text-muted-foreground">{a.durationMs} ms</span> : null}
                            </li>
                          ))}
                        </ul>
                      ) : !r.error ? <p className="text-xs text-muted-foreground">{t("admin:automations.runs.noActions")}</p> : null}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
